import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import StickyCard from "../../components/Moleculas/StickyCard/StickyCard";
import ButtonsCrud from "../../components/Catalogs/ButtonsCrud";
import Dates from "./Dates";
import Location from "./Location";
import GeneralSettings from "./GeneralSettings";
import HostAndSponsors from "./HostsAndSponsors";
import Graphics from "./Graphics";
import AdditionalSettings from "./AdditionalSettings";
import EventTabs from "./EventTabs";
import { useEventForm } from "../../hooks/Events/useEventForm";
import { useEventValidation } from "../../hooks/Events/useEventValidation";
import { useEventImages } from "../../hooks/Events/EventImagesContext";
import { EventImagesProvider } from "../../hooks/Events/EventImagesContext";
import { useEventLoader } from "../../hooks/Events/useEventLoader";
import { createEvent, deleteEvent, updateEvent } from "../../Services/Event";
import { toastError, toastPromise } from "../../utils/toast";
import { uploadImage } from "../../utils/UploadImage";
import { ValidationErrors } from "../../hooks/Events/useEventValidation";

const REQUIRED_SUFFIX = ' is required';

const formatValidationErrors = (errors: ValidationErrors): React.ReactNode => {
  const requiredFields: string[] = [];
  const otherErrors: string[] = [];
  
  Object.values(errors).forEach((error) => {
    if (!error) {
      return;
    }
    
    if (error.endsWith(REQUIRED_SUFFIX)) {
      const fieldLabel = error.replace(REQUIRED_SUFFIX, '');
      requiredFields.push(fieldLabel);
    } else {
      otherErrors.push(error);
    }
  });
  
  const errorMessages: (string | React.ReactNode)[] = [];
  
  if (requiredFields.length > 0) {
    const requiredMessage = requiredFields.length === 1 
      ? `${requiredFields[0]}${REQUIRED_SUFFIX}`
      : `${requiredFields.join(', ')} are required`;
    errorMessages.push(requiredMessage);
  }
  
  errorMessages.push(...otherErrors);
  
  if (errorMessages.length === 0) {
    return null;
  }
  
  return (
    <div style={{ textAlign: 'left' }}>
      {errorMessages.map((error, index) => (
        <div key={index} style={{ marginBottom: index < errorMessages.length - 1 ? '4px' : '0' }}>
          {error}
        </div>
      ))}
    </div>
  );
};

const BANNER_PATHS = {
  bannerLists: "event/banners/bannerLists/",
  bannerDetails: "event/banners/bannerDetails/",
  bannerHomePage: "event/banners/bannerHomePage/",
  bannerInvoice: "event/banners/bannerInvoice/",
} as const;

type BannerKey = keyof typeof BANNER_PATHS;

const AddEventContent = () => {
  const { event, updateEventField } = useEventForm();
  const { pathname } = useLocation();
  const { id } = useParams();
  const isEditMode = pathname.includes('edit');
  const validation = useEventValidation(undefined, isEditMode);
  const { getImages } = useEventImages();
  const navigate = useNavigate();

  useEventLoader(id, isEditMode);

  const handleImageUpload = useCallback(async (
    image: File | string | null | undefined,
    bannerKey: BannerKey,
    eventId: string
  ): Promise<string | undefined> => {
    if (image instanceof File) {
      const url = await uploadImage(image, eventId, BANNER_PATHS[bannerKey]);
      if (url) {
        updateEventField(bannerKey, url);
      }
      return url;
    }
    return typeof image === "string" ? image : undefined;
  }, [updateEventField]);

  const handleActionButton = useCallback(async (action: "saveButton" | "updateButton") => {
    validation.markAsAttempted();
    const validationResult = validation.validate(event);
    
    if (!validationResult.isValid) {
      const errorContent = formatValidationErrors(validationResult.errors);
      if (errorContent) {
        toastError(errorContent);
      }
      return;
    }

    try {
      const images = getImages();
      const eventId = event.id || event.shortId || "new";
      
      const imageUrls = await Promise.all([
        handleImageUpload(images.bannerLists, 'bannerLists', eventId),
        handleImageUpload(images.bannerDetails, 'bannerDetails', eventId),
        handleImageUpload(images.bannerHomePage, 'bannerHomePage', eventId),
        handleImageUpload(images.bannerInvoice, 'bannerInvoice', eventId),
      ]);

      const [bannerLists, bannerDetails, bannerHomePage, bannerInvoice] = imageUrls;

      const eventToSave = {
        ...event,
        ...(bannerLists && { bannerLists }),
        ...(bannerDetails && { bannerDetails }),
        ...(bannerHomePage && { bannerHomePage }),
        ...(bannerInvoice && { bannerInvoice }),
      };

      const promise = action === "saveButton" 
        ? createEvent(eventToSave) 
        : updateEvent(eventToSave);
      await toastPromise(promise);
      navigate(-1);
    } catch (error) {
      toastError(error as string);
    }
  }, [event, validation, getImages, handleImageUpload, navigate]);

  const handleDelete = useCallback(async () => {
    try {
      const promise = deleteEvent(event.id as string);
      await toastPromise(promise);
      navigate(-1);
    } catch (error) {
      toastError(error as string);
    }
  }, [event.id, navigate]);

  const mode = useMemo(() => pathname.includes('edit') ? 'edit' : 'add', [pathname]);
  
  return (
    <div className="h-full flex flex-col w-full justify-start items-start text-start text-gray-font gap-4 overflow-none">
      <GeneralSettings validation={validation} />
      <StickyCard title="DATES">
        <Dates validation={validation} />
      </StickyCard>
      {event.eventType !== 'virtual' && <StickyCard title="LOCATION">
        <Location validation={validation} />
      </StickyCard>}
      <StickyCard title="EVENT HOSTS AND SPONSORS">
        <HostAndSponsors validation={validation} />
      </StickyCard>
      <StickyCard title="EVENT TABS">
        <EventTabs />
      </StickyCard>
      <StickyCard title="GRAPHICS">
        <Graphics />
      </StickyCard>
      <StickyCard title="ADDITIONAL SETTINGS">
        <AdditionalSettings />
      </StickyCard>
      <div className="py-6 w-full">
        <ButtonsCrud
          Catalog="Event"
          mode={mode}
          id=""
          disabled={validation.hasErrors}
          actionButton={handleActionButton}
          onDelete={handleDelete}
          state={{}}
          newState={event}
        />
      </div>
    </div>
  );
};

const AddEvent = () => {
  return (
    <EventImagesProvider>
      <AddEventContent />
    </EventImagesProvider>
  );
};

export default AddEvent;
